import { ArrowLeft, Info } from 'lucide-react';
import type { MoscowItem, MoscowCategory } from '../types';

interface CategorizationViewProps {
  item: MoscowItem;
  itemIndex: number;
  totalItems: number;
  onCategorySelect: (category: MoscowCategory) => void;
  onBack: () => void;
  progress: number;
}

export function CategorizationView({
  item,
  itemIndex,
  totalItems,
  onCategorySelect,
  onBack,
  progress,
}: CategorizationViewProps) {
  const categories: { value: MoscowCategory; label: string; description: string; color: string }[] = [
    {
      value: 'Must',
      label: 'Must Have',
      description: 'Essential, non-negotiable. The project cannot succeed without this.',
      color: 'red',
    },
    {
      value: 'Should',
      label: 'Should Have',
      description: 'Important but not essential. High value but can be deferred if needed.',
      color: 'orange',
    },
    {
      value: 'Could',
      label: 'Could Have',
      description: 'Nice-to-have. Low impact if absent. Can be included if time/resources allow.',
      color: 'yellow',
    },
    {
      value: 'Wont',
      label: "Won't Have",
      description: 'Explicitly out of scope for now. May be considered in future iterations.',
      color: 'gray',
    },
  ];

  const getColorClasses = (color: string) => {
    switch (color) {
      case 'red':
        return 'bg-[var(--coral-dim)] border-[var(--coral)] hover:bg-[var(--coral-dim)] hover:border-[var(--coral)] text-[var(--coral)]';
      case 'orange':
        return 'bg-[var(--mustard-dim)] border-[var(--mustard)] hover:bg-[var(--mustard-dim)] hover:border-[var(--mustard)] text-[var(--mustard)]';
      case 'yellow':
        return 'bg-[var(--mustard-dim)] border-[var(--mustard)] hover:bg-[var(--mustard-dim)] hover:border-[var(--mustard)] text-[var(--mustard)]';
      case 'gray':
        return 'bg-[var(--surface)] border-[var(--rule)] hover:bg-[var(--hover-tint)] hover:border-[var(--rule)] text-[var(--text-pri)]';
      default:
        return 'bg-[var(--surface)] border-[var(--rule)] hover:bg-[var(--hover-tint)] text-[var(--text-pri)]';
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-[var(--bg-raised)] shadow-sm border border-[var(--rule)] p-6">
        {/* Progress */}
        <div className="mb-6">
          <div className="flex justify-between text-sm text-[var(--text-sec)] mb-2">
            <span>Item {itemIndex + 1} of {totalItems}</span>
            <span>{Math.round(progress)}%</span>
          </div>
          <div className="w-full bg-[var(--hover-tint)] rounded-full h-2">
            <div
              className="bg-[var(--slate)] h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Current Item */}
        <div className="mb-6 p-4 bg-[var(--slate-dim)] border border-[var(--slate)]">
          <h2 className="text-xl font-bold text-[var(--text-pri)]">{item.name}</h2>
        </div>

        {/* Category Definitions */}
        <div className="mb-6">
          <div className="flex items-start gap-2 mb-4">
            <Info className="h-5 w-5 text-[var(--text-ter)] flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-lg font-semibold text-[var(--text-pri)]">MoSCoW Categories</h3>
              <p className="text-sm text-[var(--text-sec)] mt-1">
                Select the category that best fits this item.
              </p>
            </div>
          </div>
        </div>

        {/* Category Buttons */}
        <div className="space-y-3 mb-6">
          {categories.map((category) => (
            <button
              key={category.value}
              onClick={() => onCategorySelect(category.value)}
              className={`w-full p-4  border-2 text-left transition-all ${getColorClasses(category.color)}`}
            >
              <div className="font-semibold text-lg mb-1">{category.label}</div>
              <div className="text-sm opacity-90">{category.description}</div>
            </button>
          ))}
        </div>

        {/* Navigation */}
        <div className="flex gap-3">
          {itemIndex > 0 && (
            <button
              onClick={onBack}
              className="flex items-center gap-2 px-4 py-2 bg-[var(--hover-tint)] text-[var(--text-sec)] hover:bg-[var(--hover-tint)] transition-colors"
            >
              <ArrowLeft className="h-5 w-5" />
              Back
            </button>
          )}
        </div>
      </div>
    </div>
  );
}


